// pages/sections/ClientBillingReportModal.js
// Read-only Client Billing Report popup (WIP, billings and disbursements, current
// vs prior year), ported from swa-botfieldBilling's report. Opened from the Client
// Billing Review section:
//   * header button  — no `clientCodes`: every client the viewer owns, with search
//     and paging;
//   * per-client button — `clientCodes={[code]}`: just those client(s); the Billing /
//     Relationship Group views expand to each group's FULL membership, with members
//     outside the viewer's book labelled and de-emphasised.
// Data comes from /api/diClientBillingReport/{period}, already scoped server-side.

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  METRICS, SCOPES, GROUP_VIEWS, ciGet, metricValue, fmtMetricVal, yoyDelta,
  reportDateRange, buildEntities, sortEntities, compareCodes, useClientBillingReport,
} from './clientBillingReportUtil';
import './ClientBillingReportModal.css';

const PAGE_SIZE = 25;

export default function ClientBillingReportModal({ open, onClose, period, clientCodes, title }) {
  const [groupBy, setGroupBy] = useState('client');
  const [scopeKey, setScopeKey] = useState('YTD');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const [sort, setSort] = useState({ key: 'code', dir: 'asc' });
  const scope = SCOPES[scopeKey] || SCOPES.YTD;

  const { data, error, loading } = useClientBillingReport(period, open);

  // Escape closes; the page behind doesn't scroll while the popup is up.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // A different client (or the modal reopening) starts from a clean view.
  const codesKey = Array.isArray(clientCodes) ? clientCodes.join('|') : '';
  useEffect(() => {
    if (!open) return;
    setGroupBy('client'); setSearch(''); setPage(1); setExpanded(new Set());
  }, [open, codesKey]);
  useEffect(() => { setPage(1); setExpanded(new Set()); }, [groupBy]);

  const focusCodes = useMemo(
    () => (codesKey ? new Set(codesKey.split('|').filter(Boolean)) : null),
    [codesKey]
  );
  const scoped = !!focusCodes;

  const entities = useMemo(() => {
    if (!data) return [];
    return buildEntities(data.rows, { groupBy, groupings: data.groupings, focusCodes });
  }, [data, groupBy, focusCodes]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return entities;
    return entities.filter(e =>
      String(e.code || '').toLowerCase().includes(s) ||
      String(e.name || '').toLowerCase().includes(s) ||
      (e.isGroup && e.members.some(m =>
        String(ciGet(m, 'ClientCode') || '').toLowerCase().includes(s) ||
        String(ciGet(m, 'ClientName') || '').toLowerCase().includes(s))));
  }, [entities, search]);

  const sorted = useMemo(() => sortEntities(filtered, sort, scope), [filtered, sort, scope]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const onSort = key => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const toggle = key => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  if (!open) return null;

  const noun = groupBy === 'client' ? 'clients' : groupBy === 'billing' ? 'billing groups' : 'relationship groups';

  return createPortal(
    <div className="cbrm-backdrop" onMouseDown={onClose}>
      <div
        className="cbrm-card"
        role="dialog"
        aria-modal="true"
        aria-label="Client Billing Report"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="cbrm-header">
          <div className="cbrm-title-block">
            <h3 className="cbrm-title">Client Billing Report</h3>
            {title && <div className="cbrm-subtitle">{title}</div>}
          </div>

          {!scoped && data && (
            <div className="cbrm-search">
              <i className="fas fa-search cbrm-search-icon" aria-hidden="true" />
              <input
                type="text"
                className="cbrm-search-input"
                placeholder="Search by code or name…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
          )}

          <div className="cbrm-seg" role="group" aria-label="Grouping view">
            {GROUP_VIEWS.map(v => (
              <button
                key={v.key}
                type="button"
                className={`cbrm-seg-btn ${groupBy === v.key ? 'active' : ''}`}
                onClick={() => setGroupBy(v.key)}
              >{v.label}</button>
            ))}
          </div>

          <div className="cbrm-period">
            <span className="cbrm-daterange" title="Reporting window for the selected period">{reportDateRange(scopeKey)}</span>
            <div className="cbrm-seg" role="group" aria-label="Period scope">
              {Object.keys(SCOPES).map(k => (
                <button
                  key={k}
                  type="button"
                  className={`cbrm-seg-btn ${scopeKey === k ? 'active' : ''}`}
                  onClick={() => setScopeKey(k)}
                >{SCOPES[k].label}</button>
              ))}
            </div>
          </div>

          <button type="button" className="cbrm-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="cbrm-body">
          {loading && <div className="cbrm-msg">Loading report…</div>}
          {error && <div className="cbrm-msg cbrm-msg--error">Couldn&apos;t load the billing report: {error}</div>}
          {data && sorted.length === 0 && (
            <div className="cbrm-msg">
              {search ? `No ${noun} match “${search}”.` : `No billing report data found for ${scoped ? 'this client' : `your ${noun}`}.`}
            </div>
          )}
          {data && sorted.length > 0 && (
            <table className="cbrm-table">
              <thead>
                <tr>
                  <th className="cbrm-col-expand" />
                  <SortTh label={groupBy === 'client' ? 'Client' : 'Group'} sortKey="code" sort={sort} onSort={onSort} />
                  <SortTh label="Office" sortKey="office" sort={sort} onSort={onSort} />
                  <th className="cbrm-col-roles">Client Roles</th>
                  {METRICS.map(m => (
                    <SortTh key={m.key} label={m.label} sortKey={`metric:${m.key}`} sort={sort} onSort={onSort} className="cbrm-col-metric" />
                  ))}
                </tr>
              </thead>
              {paged.map(e => {
                const canExpand = e.isGroup && e.members.length > 1;
                const isOpen = expanded.has(e.key);
                const outside = e.members.filter(m => m.inScope === false).length;
                return (
                  <tbody key={e.key}>
                    <tr
                      className={`cbrm-row ${canExpand ? 'cbrm-row--click' : ''} ${isOpen ? 'cbrm-row--open' : ''}`}
                      onClick={canExpand ? () => toggle(e.key) : undefined}
                    >
                      <td className="cbrm-cell-expand">
                        {canExpand && <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} aria-hidden="true" />}
                      </td>
                      <td className="cbrm-client-cell">
                        <span className="cbrm-name-wrap">
                          <ClientChip code={e.code} name={e.name} />
                          {e.isGroup && e.memberCount > 1 && (
                            <span
                              className="cbrm-badge"
                              title={outside ? `${outside} of these clients are outside your book` : undefined}
                            >{e.memberCount} clients</span>
                          )}
                        </span>
                      </td>
                      <td>{e.office || '—'}</td>
                      <td className="cbrm-col-roles">
                        <RoleChips originator={e.originator} partner={e.partner} manager={e.manager} />
                      </td>
                      {METRICS.map(m => (
                        <td key={m.key} className="cbrm-col-metric"><MetricCell metrics={e.metrics} metric={m} scope={scope} /></td>
                      ))}
                    </tr>
                    {canExpand && isOpen && [...e.members]
                      .sort((a, b) => compareCodes(ciGet(a, 'ClientCode'), ciGet(b, 'ClientCode')))
                      .map(m => {
                        const foreign = m.inScope === false;
                        const code = ciGet(m, 'ClientCode');
                        return (
                          <tr key={`${e.key}:${code}`} className={`cbrm-member-row ${foreign ? 'cbrm-member-row--foreign' : ''}`}>
                            <td className="cbrm-cell-expand" />
                            <td className="cbrm-client-cell">
                              <span className="cbrm-name-wrap">
                                <ClientChip code={code} name={ciGet(m, 'ClientName')} />
                                {foreign && (
                                  <span className="cbrm-badge cbrm-badge--foreign" title="You are not the Originator, Partner, or Manager on this client">
                                    Outside your book
                                  </span>
                                )}
                              </span>
                            </td>
                            <td>{ciGet(m, 'ClientOffice') || '—'}</td>
                            <td className="cbrm-col-roles">
                              <RoleChips originator={ciGet(m, 'ClientOriginator')} partner={ciGet(m, 'ClientPartner')} manager={ciGet(m, 'ClientManager')} />
                            </td>
                            {METRICS.map(mm => (
                              <td key={mm.key} className="cbrm-col-metric"><MetricCell metrics={m} metric={mm} scope={scope} /></td>
                            ))}
                          </tr>
                        );
                      })}
                  </tbody>
                );
              })}
            </table>
          )}
        </div>

        {data && sorted.length > PAGE_SIZE && (
          <div className="cbrm-pagination">
            <button type="button" disabled={safePage <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page">
              <i className="fas fa-chevron-left" aria-hidden="true" />
            </button>
            <span className="cbrm-page-info">
              Page {safePage} of {totalPages} <span className="cbrm-page-total">({sorted.length.toLocaleString()} {noun})</span>
            </span>
            <button type="button" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)} aria-label="Next page">
              <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Clickable, sortable column header (asc first, then toggles).
function SortTh({ label, sortKey, sort, onSort, className }) {
  const active = sort.key === sortKey;
  return (
    <th className={className}>
      <button type="button" className={`cbrm-sort ${active ? 'active' : ''}`} onClick={() => onSort(sortKey)} title={`Sort by ${label}`}>
        <span>{label}</span>
        <span className="cbrm-sort-arrow">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
    </th>
  );
}

// One metric cell: CY value (YoY badge pinned to its corner) over the PY value.
function MetricCell({ metrics, metric, scope }) {
  const cyv = metricValue(metrics, scope.cy, metric);
  const pyv = metricValue(metrics, scope.py, metric);
  const delta = yoyDelta(metric, cyv, pyv);
  return (
    <div className="cbrm-metric-cell">
      <span className="cbrm-chip cbrm-chip--cy">
        <span className="cbrm-chip-lbl">CY</span>{fmtMetricVal(metric, cyv)}
        {delta && <span className={`cbrm-delta ${delta.dir ? `cbrm-delta--${delta.dir}` : ''}`}>{delta.text}</span>}
      </span>
      <span className="cbrm-chip cbrm-chip--py"><span className="cbrm-chip-lbl">PY</span>{fmtMetricVal(metric, pyv)}</span>
    </div>
  );
}

function ClientChip({ code, name }) {
  return (
    <span className="cbrm-client-chip" title={`${code || ''} — ${name || ''}`}>
      <span className="cbrm-client-code">{code}</span>
      <span className="cbrm-client-name">{name}</span>
    </span>
  );
}

function RoleChips({ originator, partner, manager }) {
  return (
    <div className="cbrm-roles">
      <span className="cbrm-role cbrm-role--o" title="Client Originator">{originator || '—'}</span>
      <span className="cbrm-role cbrm-role--p" title="Client Partner">{partner || '—'}</span>
      <span className="cbrm-role cbrm-role--m" title="Client Manager">{manager || '—'}</span>
    </div>
  );
}
